// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 156393 $
//  Purpose     : implements layout transformations as sagas
//                transformation animations are emitted as sendFrame redux actions
// =============================================================================
// ---Third-party imports
import {buffers} from 'redux-saga'
import {
  call,
  cancel,
  put,
  putResolve,
  take,
  takeLatest,
  takeEvery,
  all,
  fork,
  throttle,
  actionChannel,
  spawn,
} from 'redux-saga/effects'
import now from 'performance-now'

// ---Framework imports
import {Actions} from 'uix/registries'
import {get, items, range, zip, mapItems, has, isEqual} from 'ldash'
import {select, delay} from 'uix/sagas'
import log from 'uix/logging'
import {getRunId} from 'uix/selectors'
import {layoutId} from './layouts'
import {runMorph, smoothScroll, sendFrame, animationDone, setI3F} from './actions'
import Api from 'uix/api'
// ---Globals

// see https://gist.github.com/gre/1650294
let EIn = power => t => t ** power
let EOut = power => t => 1 - Math.abs((t - 1) ** power)
let EInOut = power => t => (t < 0.5 ? EIn(power)(t * 2) / 2 : EOut(power)(t * 2 - 1) / 2 + 0.5)

let Easing = {
  linear: EInOut(1),
  easeInQuad: EIn(2),
  easeOutQuad: EOut(2),
  easeInOutQuad: EInOut(2),
  easeInCubic: EIn(3),
  easeOutCubic: EOut(3),
  easeInOutCubic: EInOut(3),
  easeInQuart: EIn(4),
  easeOutQuart: EOut(4),
  easeInOutQuart: EInOut(4),
  easeInQuint: EIn(5),
  easeOutQuint: EOut(5),
  easeInOutQuint: EInOut(5),
}

let sigmoid = steps => {
  // build a 0-1 sigmoidal for a absolute number of steps
  // https://tf.lab.vls.io/notebooks/Sigmoidal.ipynb

  let LEFT = -10
  let RIGHT = 20
  let FLATTNESS = 0.2

  let sigmoid_raw = x => x / (1 + Math.abs(x) * FLATTNESS)
  let sigmoid_norm = x => (sigmoid_raw(x) - min_y) * normalize_1
  let min_y = sigmoid_raw(LEFT)
  let max_y = sigmoid_raw(RIGHT)
  let normalize_1 = (max_y - min_y) ** -1

  return i => sigmoid_norm(LEFT + (i * (RIGHT - LEFT)) / steps)
}

let linear2D = (start, end, steps) => {
  let step_x = (end.x - start.x) / steps
  let step_y = (end.y - start.y) / steps
  return {
    fx: i => i * step_x,
    fy: i => i * step_y,
  }
}

let linear1D = (start, end, steps) => {
  let step_x = (end - start) / steps
  return i => i * step_x
}

let accel_decel2D = (start, end, steps) => {
  let range_x = end.x - start.x
  let range_y = end.y - start.y
  // our sigmoid looks faster op-wise: maybe measure this really ...
  //~ let s = i => Easing.easeInOutCubic(i / steps)
  let s = sigmoid(steps)
  return {
    fx: i => range_x * s(i),
    fy: i => range_y * s(i),
  }
}

let timestampGenerator = (duration, fps) => {
  let steps = parseInt(duration * fps)
  // frame setup
  let OFFSET_TS = 50
  let step_ts = (duration / steps) * 1000
  let start_ts = OFFSET_TS + now()

  return {steps, fts: i => start_ts + i * step_ts}
}

function* launchFrames(generateFrame, steps, viewerId, animationId, appendNativeScroll) {
  /* run a viewer animation sequence given by frame generator
   */
  // XXX: reasoning for 1-based indexing missing
  for (let i of range(1, steps + 1)) {
    // generate a new frame
    let frame = generateFrame(i)

    // if the calculation was faster than the framerate, sleep remaining time delta
    let remaining_frame = frame.ts - now()
    if (i < steps && remaining_frame > 0) {
      //~ log.debug('remaining %d ms', remaining_frame)
      yield delay(remaining_frame)
    }

    //~ log('send frame of %o [%s/%s] %o', animationId, i, steps, frame)
    yield putResolve(
      sendFrame({
        ...frame,
        remaining: remaining_frame,
        last: i == steps, // is last frame?
        viewerId,
        animationId,
      })
    )
  }

  if (appendNativeScroll) {
    yield putResolve(
      sendFrame({
        last: true, // pseudo last frame
        x: appendNativeScroll.x,
        y: appendNativeScroll.y,
        nativeSmoothScroll: true,
        viewerId,
        animationId,
      })
    )
  }
  yield put(animationDone(viewerId, animationId))
}

// calc duration from distance
let calcDuration = (start, end) => {
  let PAGES_PER_SEC = 4
  let distance = ((start.x - end.x) ** 2 + (start.y - end.y) ** 2) ** 0.5
  if (distance > 8) {
    PAGES_PER_SEC = 12
  }
  return distance / PAGES_PER_SEC
}
// process smothScroll action
function* runSmoothScroll({payload, meta}) {
  // generate frames for scroll path

  //~ log('Scroll: %o', payload)

  let {
    start,
    end,
    startZoom,
    endZoom,
    duration,
    fps,
    easing,
    viewerId,
    appendNativeScroll, // {x, y} coord of a final frame that triggers a native smooth scroll
  } = payload

  if (duration == 'auto') {
    duration = calcDuration(start, end)
  }

  let {steps, fts} = timestampGenerator(duration, fps)

  let speed_f = accel_decel2D
  if (easing === 'linear') {
    speed_f = linear2D
  }

  let {fx, fy} = speed_f(start, end, steps)

  let fzoom = startZoom ? linear1D(startZoom, endZoom, steps) : undefined

  let generateFrame = i => ({
    i,
    ts: fts(i),
    x: start.x + fx(i),
    y: start.y + fy(i),
    zoom: startZoom ? parseInt(startZoom + fzoom(i)) : undefined,
  })

  yield launchFrames(generateFrame, steps, viewerId, meta.animationId, appendNativeScroll)
}

let gutterPump = (startLayout, steps) => {
  // here we rebuild a full layout from list of rectangles (pages)
  let {pages, align, bushMode, limit} = startLayout

  // local import preserves hot reload of layouts
  let ll = require('./layouts')
  let PUMP = 0.5 // pump = 2 is one pagewidth
  return i => ({
    ...ll.Layout(pages, {
      ...startLayout.options,
      gutter_x: i > steps / 2 ? ((steps - i) / steps) * PUMP : (i / steps) * PUMP,
      gutter_y: i > steps / 2 ? ((steps - i) / steps) * PUMP : (i / steps) * PUMP,
    }),
  })
}

let linearMorph = (startLayout, endLayout, steps) => {
  // here we transform the areas of startLayout to endLayout
  let {pages, align, bushMode} = startLayout

  let index = startLayout.areas.map(a1 => {
    let delta_x = 0
    let delta_y = 0

    if (endLayout.byId[a1.id]) {
      let a2 = endLayout.byId[a1.id]
      delta_x = (a2.x - a1.x) / steps
      delta_y = (a2.y - a1.y) / steps
    }
    return {delta_x, delta_y}
  })

  return frame_idx => {
    let newAreas = startLayout.areas.map((area, area_idx) => {
      let x = area.x + index[area_idx].delta_x * frame_idx
      let y = area.y + index[area_idx].delta_y * frame_idx

      return {
        ...area,
        x,
        y,
        center: {
          x: x + area.width / 2,
          y: y + area.height / 2,
        },
      }
    })
    if (frame_idx == steps) {
      return endLayout
    } else {
      return {
        ...endLayout,
        areas: newAreas,
        //
        id: layoutId(),
      }
    }
  }
}

function* morph({payload, meta}) {
  /* generic transform from startLayout to endLayout
  XXX: we could start a frame precalc in a webworker
  */
  //~ log('Morph: %o', payload)
  let {
    startLayout,
    endLayout,
    start,
    end,
    startZoom,
    endZoom,
    duration,
    fps: framesPerSec,
    viewerId,
  } = payload

  let {animationId} = meta

  let {steps, fts} = timestampGenerator(duration, framesPerSec)

  let animation = {steps, flayout: i => startLayout}

  //~ log(Layout(endLayout.pages, endLayout.options.align, {}))
  if (!endLayout) {
    animation.flayout = gutterPump(startLayout, steps)
  } else {
    animation.flayout = linearMorph(startLayout, endLayout, steps)
  }

  // maybe also move
  let f_move = i => {}
  if (start && end) {
    let {fx, fy} = accel_decel2D(start, end, steps)
    f_move = i => ({
      x: start.x + fx(i),
      y: start.y + fy(i),
    })
  }

  // maybe also zoom
  let fzoom = startZoom ? linear1D(startZoom, endZoom, steps) : undefined

  let generateFrame = i => ({
    i,
    ts: fts(i),
    layout: {...animation.flayout(i), animationId, animationLast: i === steps},
    zoom: startZoom ? parseInt(startZoom + fzoom(i)) : undefined,
    ...f_move(i),
  })

  yield launchFrames(generateFrame, animation.steps, viewerId, animationId)
}

function* startSmoothScroll(action) {
  let task = yield fork(runSmoothScroll, action)
  // if we get another SMOOTH_SCROLL, abort
  yield take(smoothScroll)
  //~ log('canceling')
  yield cancel(task)
}

function* watchSmoothScroll() {
  let buffer = buffers.fixed(10)
  let chan = yield actionChannel('SMOOTH_SCROLL', buffer)

  let lastScrollAction = null
  while (true) {
    let action = yield take(chan)
    // XXX: this timestamp game is crazy
    if (lastScrollAction) {
      let endTimestamp = lastScrollAction.meta.timestamp + lastScrollAction.payload.duration * 1000
      if (action.meta.timestamp <= endTimestamp) {
        let start = {x: lastScrollAction.payload.end.x, y: lastScrollAction.payload.end.y}
        let payload = {...action.payload, start}
        action = {...action, payload}
      }
    }

    yield runSmoothScroll(action)
    lastScrollAction = action
  }
}

function* startSmoothScrollWait(waitForIds, smoothScrollAction) {
  if (!waitForIds) {
    yield put(smoothScrollAction)
  } else {
    //~ log('wait for %o', waitForIds)
    let {animationId, viewerId} = waitForIds
    while (true) {
      let action = yield take(animationDone)

      if (isEqual(action.payload, waitForIds)) {
        yield put(smoothScrollAction)
      } else if (action.payload.animationId > animationId && action.payload.viewerId === viewerId) {
        break
      }
    }
  }
}

function* startSmoothScrollSeq() {
  let lastParamsAll = {}

  while (true) {
    let {payload, meta} = yield take('SMOOTH_SCROLL_SEQ')

    let lastParams = null
    let waitForIds = null
    if (payload.sequenceStart) {
      waitForIds = null
    } else {
      lastParams = lastParamsAll[payload.viewerId].payload
      if (!lastParams) throw 'need sequenceStart in action payload'

      waitForIds = {
        viewerId: payload.viewerId,
        animationId: lastParamsAll[payload.viewerId].animationId,
      }
    }

    // interpolate params from last operation
    if (!payload.start && lastParams) {
      payload = {...payload, start: lastParams.end || lastParams.start}
    }

    if (!payload.startZoom && lastParams) {
      payload = {...payload, startZoom: lastParams.endZoom || lastParams.startZoom}
    }

    if (!payload.fps && lastParams) {
      payload = {...payload, fps: lastParams.fps}
    }

    let nextSmoothScrollAction = smoothScroll(payload)
    lastParamsAll[payload.viewerId] = {
      payload,
      animationId: nextSmoothScrollAction.meta.animationId,
    }

    yield fork(startSmoothScrollWait, waitForIds, nextSmoothScrollAction)
  }
}

function* startMorph(action) {
  let task = yield fork(morph, action)
  yield take(runMorph)
  yield cancel(task)
}

function* _fetchI3F({payload}) {
  let {manifest} = payload
  let {data, error} = yield Api.get(
    manifest,
    {},
    {timeout: Infinity, withCredentials: false, headers: {}}
  )
  if (error) {
    yield put(Actions.errorNotification({error, header: 'Manifest Load Error'}))
  } else {
    yield put(setI3F({manifest, data}))
  }
}

function* watchGetI3f() {
  let chan = yield actionChannel('GET_I3F', buffers.fixed(10))
  while (true) {
    let action = yield take(chan)
    yield call(_fetchI3F, action)
  }
}

export const sagas = {
  //~ smoothScroll: takeLatest(smoothScroll, startSmoothScroll),
  watchSmoothScroll: watchSmoothScroll(),
  //~ smoothScrollSeq: startSmoothScrollSeq(),
  morph: takeLatest(runMorph, startMorph),
  watchGetI3f: watchGetI3f(),
}
